import { ISelectItem } from "@components/inputs/select/Select.types";
import { ifAll, TGetValueFn } from "@components/smart/FieldInfo";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { Metadata } from "@evala/odata-metadata";
import { createPath, IEntity } from "@odata/BindingContext";
import { getExpandedEnumProps } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    GovernmentInstitutionEntity,
    ICompanyEntity,
    IPrPayrollSettingEntity,
    IPrSubmissionEntity,
    IPrSubmissionSubtypeEntity,
    OdataActionName,
    OdataFunctionName,
    PrCSSZSubmissionCompanyEntity,
    PrCSSZSubmissionDetailEntity,
    PrEmploymentEntity,
    PrPayrollSettingEntity,
    PrSocialEventEntity,
    PrSocialSecurityDetailEntity,
    PrSubmissionEntity,
    PrSubmissionRegZecEntity,
    PrSubmissionSubtypeEntity,
    RegZecEmploymentEntity,
} from "@odata/GeneratedEntityTypes";
import {
    PrSocialEventTypeCode,
    PrSubmissionStatusCode,
    PrSubmissionSubtypeCode,
    PrSubmissionTypeCode,
    PrSubmissionValidationStatusCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { OData } from "@odata/OData";
import { getEntitySetFromEntityType, transformToODataString } from "@odata/OData.utils";
import { ODataQueryResult } from "@odata/ODataParser";
import { getDefByEntityType } from "@pages/getDefByEntityType";
import { IGetDefinition } from "@pages/PageUtils";
import { getEmployeeSummaryItemDef } from "@pages/payroll/employee/Employee.utils";
import { PrSubmissionGroup } from "@pages/payroll/submission/Submission.def";
import { PrCSSZSubmissionEntity } from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import { getFileFromSuccessResponse } from "@utils/FileStorage";
import { isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import { saveAs } from "file-saver";

import { DASH_CHARACTER } from "../../../constants";
import { ValueType } from "../../../enums";
import i18n from "../../../i18n";
import i18next from "../../../i18n";
import { Model } from "../../../model/Model";
import { Theme } from "../../../theme";
import { FormStorage } from "../../../views/formView/FormStorage";

export const PrSubmissionEnumTranslationNamespaces = [
    getEnumNameSpaceName(EntityTypeName.PrSubmissionType),
    getEnumNameSpaceName(EntityTypeName.PrSubmissionSubtype),
    getEnumNameSpaceName(EntityTypeName.PrSubmissionStatus),
    getEnumNameSpaceName(EntityTypeName.PrSubmissionValidationStatus),
];

export function getSubmissionEntityType(type: PrSubmissionTypeCode, subType?: PrSubmissionSubtypeCode): EntityTypeName {
    switch (type) {
        case PrSubmissionTypeCode.EmployersHealthInsurancePaymentOverview:
            return EntityTypeName.PrPppzSubmission;
        case PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification:
            return EntityTypeName.PrHozSubmission;
        case PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication:
            switch (subType) {
                case PrSubmissionSubtypeCode.NempriMaternityLeave:
                    return EntityTypeName.PrCSSZSubmissionMaternityLeave;
                case PrSubmissionSubtypeCode.NempriPaternityLeave:
                    return EntityTypeName.PrCSSZSubmissionPaternityLeave;
                case PrSubmissionSubtypeCode.NempriCaregiving:
                    return EntityTypeName.PrCSSZSubmissionCaregiving;
                case PrSubmissionSubtypeCode.NempriLongTermCaregiving:
                    return EntityTypeName.PrCSSZSubmissionLongtermCaregiving;
                case PrSubmissionSubtypeCode.NempriIllnessLeave:
                    return EntityTypeName.PrCSSZSubmissionIllnessLeave;
            }
            break;
        case PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance:
            return EntityTypeName.PrCsszOzuspojSubmission;
        case PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork:
            return EntityTypeName.PrCsszHzupnSubmission;
        case PrSubmissionTypeCode.EmployeeRegistration:
            return EntityTypeName.PrSubmissionRegZec;
        case PrSubmissionTypeCode.MonthlyReport:
            return EntityTypeName.PrJmhzSubmission;
    }
    return null;
}

export function getEntitySetFromType(metadata: Metadata, type: PrSubmissionTypeCode, subType: PrSubmissionSubtypeCode): EntitySetName {
    const entityType = getSubmissionEntityType(type, subType);
    if (!entityType) {
        return null;
    }
    return getEntitySetFromEntityType(metadata, entityType);
}

export function getNempriSubmissionSubTypeFromSocialEventType(eventType: PrSocialEventTypeCode): PrSubmissionSubtypeCode {
    switch (eventType) {
        case PrSocialEventTypeCode.IllnessLeave:
            return PrSubmissionSubtypeCode.NempriIllnessLeave;
        case PrSocialEventTypeCode.PaternityLeave:
            return PrSubmissionSubtypeCode.NempriPaternityLeave;
        case PrSocialEventTypeCode.CaregivingAllowance:
            return PrSubmissionSubtypeCode.NempriCaregiving;
        case PrSocialEventTypeCode.LongtermCaregivingAllowance:
            return PrSubmissionSubtypeCode.NempriLongTermCaregiving;
        case PrSocialEventTypeCode.MaternityLeave:
            return PrSubmissionSubtypeCode.NempriMaternityLeave;
        case PrSocialEventTypeCode.Custody:
        case PrSocialEventTypeCode.Imprisonment:
        case PrSocialEventTypeCode.ParentalLeave:
        default:
            // these new of social events dont have NEMPRI or any other submission
            return null;
    }
}

export enum PrSubmissionTabs {
    Ready = "Ready",
    Waiting = "Waiting",
    Finished = "Finished"
}

export function getMainTypeLabel(mainType: PrSubmissionTypeCode): string {
    if (!mainType) {
        return DASH_CHARACTER;
    }
    const key = `Submission:Types.${mainType}`;
    return i18n.exists(key) ? i18n.t(key) : getEnumDisplayValue(EntityTypeName.PrSubmissionType, mainType);
}

export function getSubTypeLabel(type: PrSubmissionTypeCode, subtype: PrSubmissionSubtypeCode): string {
    const key = `Submission:SubTypes.${type}_${subtype}`;
    return i18n.exists(key) ? i18n.t(key)
        : (subtype ? getEnumDisplayValue(EntityTypeName.PrSubmissionSubtype, subtype) : DASH_CHARACTER);
}

export function getColorFromStatus(status: PrSubmissionStatusCode): keyof Theme {
    switch (status) {
        case PrSubmissionStatusCode.Submitted:
            return "C_SEM_text_good";
        case PrSubmissionStatusCode.NotSubmitted:
        case PrSubmissionStatusCode.Processing:
            return "C_SEM_text_warning";
        case PrSubmissionStatusCode.Error:
        case PrSubmissionStatusCode.Rejected:
            return "C_SEM_text_bad";
    }
    return null;
}

export const getColorFromValidationStatus = (status: PrSubmissionValidationStatusCode): keyof Theme => {
    switch (status) {
        case PrSubmissionValidationStatusCode.Valid:
            return "C_SEM_text_good";
        case PrSubmissionValidationStatusCode.Invalid:
        case PrSubmissionValidationStatusCode.Failed:
            return "C_SEM_text_bad";
        case PrSubmissionValidationStatusCode.InProgress:
            return "C_SEM_text_warning";
        case PrSubmissionValidationStatusCode.NotValidated:
            return "C_SEM_el_neutral";
    }
    return null;
};

function getStatusFromTabId(tabId: PrSubmissionTabs): PrSubmissionStatusCode[] {
    switch (tabId) {
        case PrSubmissionTabs.Ready:
            return [
                PrSubmissionStatusCode.NotSubmitted
            ];
        case PrSubmissionTabs.Waiting:
            return [
                PrSubmissionStatusCode.Processing
            ];
        case PrSubmissionTabs.Finished:
            return [
                PrSubmissionStatusCode.Rejected,
                PrSubmissionStatusCode.Error,
                PrSubmissionStatusCode.Submitted,
            ];
    }
}

export const getSubmissionSubtypes = memoizeOne(async (oData: OData): Promise<IPrSubmissionSubtypeEntity[]> => {
    const wrapper = oData.getEntitySetWrapper(EntitySetName.PrSubmissionSubtypes);
    const query = wrapper.query()
        .select(
            PrSubmissionSubtypeEntity.Code,
            PrSubmissionSubtypeEntity.MpsvCode,
            PrSubmissionSubtypeEntity.Name
        )
        .orderBy(PrSubmissionSubtypeEntity.MpsvCode, false);

    const data = await query.fetchData<IPrSubmissionSubtypeEntity[]>();
    return data.value ?? [];
});

export function getSubtypeSelectItems(mainType: PrSubmissionTypeCode, allSubtypes: Map<PrSubmissionSubtypeCode, IPrSubmissionSubtypeEntity>, codes: PrSubmissionSubtypeCode[]): ISelectItem[] {
    let items: ISelectItem[] = codes.map(code => {
        const subTypeEntity = allSubtypes?.get(code);
        const label = getSubTypeLabel(mainType, code);
        // Submission subtype should show also code https://solitea-cz.atlassian.net/browse/DEV-44023
        const mpsvCode = subTypeEntity?.MpsvCode ? `A${subTypeEntity?.MpsvCode}` : null;
        return {
            id: code,
            label: mpsvCode ? mpsvCode : label,
            tabularData: mpsvCode ? [mpsvCode, label] : undefined
        };
    });

    if (items.some(item => item.tabularData === null)) {
        // if all items don't have tabularData, remove the property from all items to avoid empty columns in the table
        items = items.map(({ tabularData, ...restProps }) => ({ ...restProps }));
    }
    return items;
}

export const isOfSubmissionSubType = (type: PrSubmissionSubtypeCode): TGetValueFn<boolean> => {
    return (args) => {
        const { storage } = args;
        return (storage.data.entity as IPrSubmissionEntity).SubmissionSubtypeCode === type;
    };
};

export const isOfSubmissionType = (type: PrSubmissionTypeCode): TGetValueFn<boolean> => {
    return (args) => {
        const { storage } = args;
        return (storage.data.entity as IPrSubmissionEntity).SubmissionTypeCode === type;
    };
};

export function getFilterForTabId(tabId: string): string {
    const statusFromTab = getStatusFromTabId(tabId as PrSubmissionTabs);
    if (statusFromTab?.length) {
        return `${PrSubmissionEntity.SubmissionStatusCode} in (${transformToODataString(statusFromTab, ValueType.String)})`;
    }
    return "";
}

/* Shared method for Table and Form */
export async function exportToXML(storage: Model, submissionIds: number[]): Promise<void> {
    try {
        await storage.oData.getEntitySetWrapper(EntitySetName.PrSubmissions)
            .func(OdataFunctionName.PrSubmissionsExportXml, null, {
                parameters: { SubmissionIds: submissionIds },
                parseResponse: async (response) => {
                    const file = await getFileFromSuccessResponse(null, response as any);
                    saveAs(file);
                }
            });
    } catch (e) {
        logger.error(`Failed to export submissions ${submissionIds.toString()} to xml`, e);
    }
}

export function getSubmissionDefinitionByTypes(mainType: PrSubmissionTypeCode, subType: PrSubmissionSubtypeCode): IGetDefinition {
    const entityType = getSubmissionEntityType(mainType, subType);
    return getDefByEntityType(entityType);
}

export function getOdataFunctionNameForSubmissionType(type: PrSubmissionTypeCode, subType: PrSubmissionSubtypeCode): OdataFunctionName {
    switch (type) {
        case PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication:
            switch (subType) {
                case PrSubmissionSubtypeCode.NempriMaternityLeave:
                    return OdataFunctionName.PrMaternityLeaveEventGenerateSubmission;
                case PrSubmissionSubtypeCode.NempriPaternityLeave:
                    return OdataFunctionName.PrPaternityLeaveEventGenerateSubmission;
                case PrSubmissionSubtypeCode.NempriCaregiving:
                    return OdataFunctionName.PrCaregivingEventGenerateSubmission;
                case PrSubmissionSubtypeCode.NempriLongTermCaregiving:
                    return OdataFunctionName.PrLongTermCaregivingEventGenerateSubmission;
                case PrSubmissionSubtypeCode.NempriIllnessLeave:
                    return OdataFunctionName.PrIllnessLeaveEventGenerateSubmission;
                default:
                    return null;
            }
        case PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork:
            return OdataFunctionName.PrIllnessLeaveEventGenerateHzupnSubmission;
        case PrSubmissionTypeCode.EmployeeRegistration:
            return OdataFunctionName.PrEmploymentGenerateRegZecSubmission;
        case PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification:
            return OdataFunctionName.PrEmployeeGenerateHozSubmission;
        case PrSubmissionTypeCode.MonthlyReport:
            return OdataFunctionName.PrPayrollPeriodGenerateJmhzSubmission;
        default:
            return null;
    }
}

// todo: get from OData and function name?
export function getOdataFuncEntitySetForSubmissionType(type: PrSubmissionTypeCode, subType: PrSubmissionSubtypeCode): EntitySetName {
    switch (type) {
        case PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication:
            switch (subType) {
                case PrSubmissionSubtypeCode.NempriMaternityLeave:
                    return EntitySetName.PrMaternityLeaveEvents;
                case PrSubmissionSubtypeCode.NempriPaternityLeave:
                    return EntitySetName.PrPaternityLeaveEvents;
                case PrSubmissionSubtypeCode.NempriCaregiving:
                    return EntitySetName.PrCaregivingEvents;
                case PrSubmissionSubtypeCode.NempriLongTermCaregiving:
                    return EntitySetName.PrLongTermCaregivingEvents;
                case PrSubmissionSubtypeCode.NempriIllnessLeave:
                    return EntitySetName.PrIllnessLeaveEvents;
                default:
                    return null;
            }
        case PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork:
            return EntitySetName.PrIllnessLeaveEvents;
        case PrSubmissionTypeCode.EmployeeRegistration:
            return EntitySetName.PrEmployments;
        case PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification:
            return EntitySetName.PrEmployees;
        case PrSubmissionTypeCode.MonthlyReport:
            return EntitySetName.PrPayrollPeriods;
        default:
            return null;
    }
}

export function getEmployeeSubmissionSummaryItemDef(employeeProp: string, isSingleEmployeeView?: boolean): ISummaryItem {
    const hasEmployee: TGetValueFn<boolean> = (args) => {
        return !!args.storage.getValueByPath(employeeProp)?.Id;
    };
    const commonDef = getEmployeeSummaryItemDef(employeeProp, isSingleEmployeeView);
    return {
        ...commonDef,
        isVisible: commonDef.isVisible ? ifAll(hasEmployee, commonDef.isVisible) : hasEmployee
    };
}

export function getSocialEventSubmissionSummaryItemDef(): ISummaryItem {
    const hasSocialEvent: TGetValueFn<boolean> = (args) => {
        return !!args.storage.getValueByPath(PrSubmissionEntity.SocialEvent)?.Id;
    };
    return {
        id: createPath(PrSubmissionEntity.SocialEvent, PrSocialEventEntity.NumberOurs),
        label: i18next.t("Submission:Form.SocialEvent"),
        updateFromLiveValue: true,
        isVisible: hasSocialEvent
    };
}

export function getEmploymentSummaryItemDef(): ISummaryItem {
    const pathToEmployment = createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Employment);
    const hasEmployment: TGetValueFn<boolean> = ({ storage }) => {
        return !!storage.getValueByPath(pathToEmployment)?.Id;
    };
    return {
        id: createPath(pathToEmployment, PrEmploymentEntity.NumberOurs),
        label: i18next.t("Submission:Summary.Employment"),
        isVisible: hasEmployment,
        updateFromLiveValue: true
    };
}

export const fetchPayrollSettings = memoizeOne(async (storage: FormStorage): Promise<IPrPayrollSettingEntity> => {
    const { oData } = storage;
    const query = oData.getEntitySetWrapper(EntitySetName.PrPayrollSettings).query()
        .expand(PrPayrollSettingEntity.CzechSocialSecurityAdministration, (q) => {
            q.select(
                PrSocialSecurityDetailEntity.SymbolVariable
            );
        });

    const res = await query.fetchData<IPrPayrollSettingEntity[]>();
    if (res.value?.length !== 1) {
        logger.error("There should be only one PrPayrollSettings entity");
    }
    const payrollSetting = res.value?.[0];
    if (!payrollSetting) {
        logger.error("PrPayrollSettings entity not found");
    }
    return payrollSetting;
}, () => [true/* cache forever */]);

interface ISetCompanyRelationArgs {
    socialFields?: boolean;
    insurancePayer?: boolean;
    withAddress?: boolean;
    withPhoneNumber?: boolean;
}

export async function setCompanyRelation(storage: FormStorage<IPrSubmissionEntity>, args: ISetCompanyRelationArgs): Promise<void> {
    const companyData = (storage.data.entity as IEntity).Detail?.Company;
    if (companyData?.Company?.Id) {
        // Company relation is already set, no need to set it again
        return;
    }

    const { LegalName, LegalNumber, InsurancePayerNumber } = companyData ?? {};

    const company = storage.context.getCompany() as ICompanyEntity;
    let data: IEntity = {
        Company: {
            Id: company.Id
        },
        LegalName: LegalName ?? company.LegalName,
        LegalNumber: LegalNumber ?? company.LegalNumber,
    };

    const settings = await fetchPayrollSettings(storage);

    if (args.socialFields) {
        data = {
            ...data,
            ...getExpandedEnumProps(EntityTypeName.GovernmentInstitution, company.SocialSecurityCode, PrCSSZSubmissionCompanyEntity.SocialSecurity, {
                [GovernmentInstitutionEntity.OpenDataCode]: company.SocialSecurity?.OpenDataCode,
                [GovernmentInstitutionEntity.ShortName]: company.SocialSecurity?.ShortName
            }),
            [PrCSSZSubmissionCompanyEntity.CSSZSymbolVariable]: settings.CzechSocialSecurityAdministration?.SymbolVariable
        };
    }

    if (args.insurancePayer) {
        // todo: fill insurance payer number, may be split for PPPZ and HOZ as it is slightly different regarding source of the data.
        data = {
            ...data,
            InsurancePayerNumber: InsurancePayerNumber ?? ""
        };
    }

    if (args.withAddress && !isObjectEmpty(company.LegalAddress)) {
        const { City, HouseNumber, PostalCode, Street, StreetNumber } = company.LegalAddress;
        data = {
            ...data,
            City, HouseNumber, PostalCode, Street, StreetNumber
        };
    }

    if (args.withPhoneNumber && company.CommunicationContact?.PhoneNumber) {
        data.PhoneNumber = company.CommunicationContact?.PhoneNumber;
    }

    storage.setValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.Company), data);
    storage.addActiveGroupByKey(PrSubmissionGroup.Employer);
}

export async function generatePppzSubmissions(oData: OData, periodId: number, institutions: string[], type: PrSubmissionSubtypeCode): Promise<IPrSubmissionEntity[]> {
    const wrapper = oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods);
    const parameters = {
        HealthInsuranceCompanyCodes: institutions,
        SubmissionSubtypeCode: type
    };

    const submissions = await wrapper.action(OdataActionName.PrPayrollPeriodGeneratePppzSubmissions, periodId, parameters);

    return (submissions as ODataQueryResult<IPrSubmissionEntity[]>)?.value;
}